"use client"

import type { SuperAdminClientDetail } from "@repo/types"
import { ArrowLeft, Ban, LogIn, CalendarClock, ClipboardList, Gauge, Infinity as InfinityIcon, PlayCircle, RotateCcw, Users } from "lucide-react"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { DendromapLoader } from "../../components/dendromap-loader"
import { Alert, AlertDescription } from "../../components/ui/alert"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { StatusBadge, type StatusTone } from "../../components/ui/data-table/status-badge"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table"
import { Textarea } from "../../components/ui/textarea"
import { useSubscriptionConfig } from "../../hooks/api/settings/use-settings"
import { useSuperAdminClient, useSuperAdminClientActions } from "../../hooks/api/superadmin/use-superadmin-client"
import { startImpersonation } from "../../lib/auth/impersonation"
import { superAdminClientsService } from "../../lib/api/services/superadmin-clients.service"
import { useGetCurrentUser } from "../../hooks/api/users/use-current-user"
import { MetricCard } from "../dashboard/components/metric-card"
import { ClientFeaturesCard } from "./client-features-card"
import {
  addMonths,
  apiErrorMessage,
  dateInputToIso,
  daysUntil,
  extensionBase,
  formatDate,
  formatQuota,
  statusLabel,
  toDateInputValue,
} from "./client-detail.helpers"

const statusTone = (status: string | null): StatusTone => {
  switch (status?.toUpperCase()) {
    case "ACTIVE":
      return "active"
    case "TRIAL":
      return "trial"
    case "PAST_DUE":
    case "SUSPENDED":
      return "warning"
    case "EXPIRED":
    case "CANCELLED":
      return "deleted"
    default:
      return "inactive"
  }
}

const PLAN_OPTIONS = ["FREE", "BASIC", "ADVANCED"] as const

/** Fiche client de l'espace /superadmin : synthèse, abonnement, actions, utilisateurs et parcours d'inscription. */
export function ClientDetailPage({ clientId }: { clientId: number }) {
  const { data: client, isLoading, error } = useSuperAdminClient(clientId)

  if (isLoading) return <DendromapLoader message="Chargement de la fiche client..." />
  if (error || !client) {
    return (
      <div className="space-y-4">
        <BackLink />
        <Alert variant="destructive">
          <AlertDescription>{apiErrorMessage(error, "Client introuvable.")}</AlertDescription>
        </Alert>
      </div>
    )
  }

  const echeanceDays = daysUntil(client.date_echeance)
  const displayName = client.name_societe || client.name || `Client #${client.id}`

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <BackLink />
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold text-foreground">{displayName}</h1>
          <StatusBadge tone={statusTone(client.subscription_status)} label={statusLabel(client.subscription_status)} />
          <span className="text-sm text-muted-foreground">
            {client.plan_type ?? "—"} · {client.tier_id ?? "—"}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          #{client.id} · {[client.ville, client.siret && `SIRET ${client.siret}`].filter(Boolean).join(" · ") || "—"} · créé le{" "}
          {formatDate(client.created_at)}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Parcours ce mois"
          value={formatQuota(client.current_month_parcours, client.max_parcours_per_month)}
          icon={Gauge}
          iconColor="text-primary"
          iconBgColor="bg-primary/10"
        />
        <MetricCard
          title="Utilisateurs actifs"
          value={`${client.counts.activeUsers} / ${client.licence ?? "—"}`}
          description={`${client.counts.users} compte${client.counts.users > 1 ? "s" : ""} au total`}
          icon={Users}
          iconColor="text-primary"
          iconBgColor="bg-primary/10"
        />
        <MetricCard
          title="Inventaires"
          value={client.counts.inventaires}
          icon={ClipboardList}
          iconColor="text-primary"
          iconBgColor="bg-primary/10"
        />
        <MetricCard
          title="Échéance"
          value={formatDate(client.date_echeance)}
          description={
            echeanceDays == null ? undefined : echeanceDays >= 0 ? `dans ${echeanceDays} jours` : `dépassée de ${-echeanceDays} jours`
          }
          icon={CalendarClock}
          iconColor={echeanceDays != null && echeanceDays < 0 ? "text-destructive" : "text-primary"}
          iconBgColor={echeanceDays != null && echeanceDays < 0 ? "bg-destructive/10" : "bg-primary/10"}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <SubscriptionCard client={client} />
        <ActionsCard client={client} />
      </div>

      <ClientFeaturesCard client={client} />
      <UsersCard client={client} />
      <TraceEventsCard client={client} />
    </div>
  )
}

function BackLink() {
  return (
    <Link to="/superadmin/clients" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="h-4 w-4" /> Clients
    </Link>
  )
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border py-2 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium text-foreground break-all">{value}</span>
    </div>
  )
}

function SubscriptionCard({ client }: { client: SuperAdminClientDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Abonnement</CardTitle>
        <CardDescription>État enregistré dans l'application.</CardDescription>
      </CardHeader>
      <CardContent>
        <InfoRow label="Statut" value={statusLabel(client.subscription_status)} />
        <InfoRow label="Offre / palier" value={`${client.plan_type ?? "—"} · ${client.tier_id ?? "—"}`} />
        <InfoRow label="Forfait" value={client.forfaitName ?? "—"} />
        <InfoRow label="Souscrit le" value={formatDate(client.date_subscribed)} />
        <InfoRow label="Échéance" value={formatDate(client.date_echeance)} />
        <InfoRow label="Échec de paiement" value={formatDate(client.payment_failed_at)} />
        <InfoRow label="Suspendu (impayé) le" value={formatDate(client.suspended_at)} />
        <InfoRow label="Client Stripe" value={client.customer_id ?? "—"} />
        <InfoRow label="Abonnement Stripe" value={client.sub_id ?? "—"} />
      </CardContent>
    </Card>
  )
}

function ActionsCard({ client }: { client: SuperAdminClientDetail }) {
  const actions = useSuperAdminClientActions(client.id)
  const { data: subscriptionConfig } = useSubscriptionConfig()
  const [echeance, setEcheance] = useState("")
  const [planType, setPlanType] = useState(client.plan_type ?? "ADVANCED")
  const [tierId, setTierId] = useState(client.tier_id ?? "")
  const [quota, setQuota] = useState(String(client.max_parcours_per_month ?? ""))
  const [licence, setLicence] = useState(String(client.licence ?? ""))
  const [aiLimit, setAiLimit] = useState(client.ai.clientLimit === null ? "" : String(client.ai.clientLimit))
  const [suspendOpen, setSuspendOpen] = useState(false)

  useEffect(() => {
    setEcheance(toDateInputValue(extensionBase(client.date_echeance)))
    setPlanType(client.plan_type ?? "ADVANCED")
    setTierId(client.tier_id ?? "")
    setQuota(String(client.max_parcours_per_month ?? ""))
    setLicence(String(client.licence ?? ""))
    setAiLimit(client.ai.clientLimit === null ? "" : String(client.ai.clientLimit))
  }, [client])

  const tiers = planType === "FREE" ? [] : (subscriptionConfig?.plans?.[planType]?.tiers ?? [])
  const pending = actions.updateSubscription.isPending

  const update = async (input: Parameters<typeof actions.updateSubscription.mutateAsync>[0], success: string) => {
    try {
      await actions.updateSubscription.mutateAsync(input)
      toast.success(success)
    } catch (error) {
      toast.error(apiErrorMessage(error, "Modification impossible"))
    }
  }

  const extendBy = (months: number) => setEcheance(toDateInputValue(addMonths(extensionBase(client.date_echeance), months)))
  const isSuspended = client.subscription_status === "SUSPENDED"

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Actions</CardTitle>
        <CardDescription>Chaque action est enregistrée dans le journal d'audit.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <section className="space-y-2">
          <Label htmlFor="client-echeance">Échéance</Label>
          <div className="flex flex-wrap gap-2">
            <Input id="client-echeance" type="date" value={echeance} onChange={(e) => setEcheance(e.target.value)} className="w-44" />
            <Button variant="outline" size="sm" onClick={() => extendBy(1)}>+1 mois</Button>
            <Button variant="outline" size="sm" onClick={() => extendBy(12)}>+1 an</Button>
            <Button
              size="sm"
              disabled={!echeance || pending}
              onClick={() => update({ date_echeance: dateInputToIso(echeance) }, "Échéance mise à jour")}
            >
              Enregistrer
            </Button>
          </div>
        </section>

        <section className="space-y-2">
          <Label>Offre et palier</Label>
          <div className="flex flex-wrap gap-2">
            <Select
              value={planType}
              onValueChange={(value) => {
                setPlanType(value)
                setTierId(subscriptionConfig?.plans?.[value]?.tiers?.[0]?.id ?? "")
              }}
            >
              <SelectTrigger className="w-36" aria-label="Offre"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLAN_OPTIONS.map((plan) => <SelectItem key={plan} value={plan}>{plan}</SelectItem>)}
              </SelectContent>
            </Select>
            {tiers.length > 0 && (
              <Select value={tierId} onValueChange={setTierId}>
                <SelectTrigger className="w-56" aria-label="Palier"><SelectValue placeholder="Palier" /></SelectTrigger>
                <SelectContent>
                  {tiers.map((tier) => (
                    <SelectItem key={tier.id} value={tier.id}>
                      {tier.title ? `${tier.title} (${tier.id})` : tier.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              size="sm"
              disabled={pending || (planType !== "FREE" && !tierId)}
              onClick={() =>
                update(
                  { plan_type: planType as "FREE" | "BASIC" | "ADVANCED", ...(planType !== "FREE" ? { tier_id: tierId } : {}) },
                  "Offre mise à jour",
                )
              }
            >
              Appliquer
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Change les droits dans l'application uniquement : la facturation Stripe du client n'est pas modifiée.
          </p>
        </section>

        <section className="space-y-2">
          <Label htmlFor="client-quota">Parcours par mois</Label>
          <div className="flex flex-wrap gap-2">
            <Input id="client-quota" type="number" min={-1} value={quota} onChange={(e) => setQuota(e.target.value)} className="w-28" />
            <Button
              size="sm"
              disabled={pending || quota === "" || !Number.isInteger(Number(quota))}
              onClick={() => update({ max_parcours_per_month: Number(quota) }, "Quota mis à jour")}
            >
              Enregistrer
            </Button>
            <Button variant="outline" size="sm" disabled={pending} onClick={() => update({ max_parcours_per_month: -1 }, "Accès illimité accordé")}>
              <InfinityIcon className="mr-1 h-4 w-4" /> Illimité
            </Button>
            <Button variant="outline" size="sm" disabled={pending} onClick={() => update({ reset_month_usage: true }, "Compteur du mois remis à zéro")}>
              <RotateCcw className="mr-1 h-4 w-4" /> Remettre le compteur à zéro
            </Button>
          </div>
        </section>

        <section className="space-y-2">
          <Label htmlFor="client-licence">Licences (utilisateurs max)</Label>
          <div className="flex flex-wrap gap-2">
            <Input id="client-licence" type="number" min={1} value={licence} onChange={(e) => setLicence(e.target.value)} className="w-28" />
            <Button
              size="sm"
              disabled={pending || !(Number(licence) >= 1)}
              onClick={() => update({ licence: Number(licence) }, "Licences mises à jour")}
            >
              Enregistrer
            </Button>
          </div>
        </section>

        <section className="space-y-2">
          <Label htmlFor="client-ai-limit">Analyses IA par mois</Label>
          <div className="flex flex-wrap gap-2">
            <Input
              id="client-ai-limit"
              type="number"
              min={0}
              value={aiLimit}
              placeholder={`${client.ai.source === "global" ? client.ai.limit : "—"} (global)`}
              onChange={(e) => setAiLimit(e.target.value)}
              className="w-36"
            />
            <Button
              size="sm"
              disabled={pending || aiLimit === "" || !(Number.isInteger(Number(aiLimit)) && Number(aiLimit) >= 0)}
              onClick={() => update({ ai_monthly_limit: Number(aiLimit) }, "Plafond IA mis à jour")}
            >
              Enregistrer
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pending || client.ai.clientLimit === null}
              onClick={() => update({ ai_monthly_limit: null }, "Quota IA global rétabli")}
            >
              Quota global
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {client.ai.usedThisMonth} analyse{client.ai.usedThisMonth > 1 ? "s" : ""} ce mois-ci sur{" "}
            {client.ai.limit > 0 ? client.ai.limit : "illimité"}
            {client.ai.source === "client" ? " (plafond propre à ce client)" : " (quota global)"} · 0 = illimité.
          </p>
        </section>

        <section className="space-y-2 border-t border-border pt-4">
          {isSuspended ? (
            <Button
              variant="outline"
              disabled={actions.reactivate.isPending}
              onClick={async () => {
                try {
                  await actions.reactivate.mutateAsync()
                  toast.success("Client réactivé")
                } catch (error) {
                  toast.error(apiErrorMessage(error, "Réactivation impossible"))
                }
              }}
            >
              <PlayCircle className="mr-2 h-4 w-4" /> Réactiver le client
            </Button>
          ) : (
            <Button variant="destructive" onClick={() => setSuspendOpen(true)}>
              <Ban className="mr-2 h-4 w-4" /> Suspendre le client
            </Button>
          )}
          <p className="text-xs text-muted-foreground">
            Une suspension bloque la création d'inventaires pour tout le groupe. Contrairement à une suspension pour impayé, le
            compte n'est jamais supprimé automatiquement.
          </p>
        </section>
      </CardContent>
      <SuspendDialog clientName={client.name_societe || client.name || `#${client.id}`} open={suspendOpen} onOpenChange={setSuspendOpen} clientId={client.id} />
    </Card>
  )
}

function SuspendDialog({
  clientId,
  clientName,
  open,
  onOpenChange,
}: {
  clientId: number
  clientName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { suspend } = useSuperAdminClientActions(clientId)
  const [reason, setReason] = useState("")

  const confirm = async () => {
    try {
      await suspend.mutateAsync({ reason: reason.trim() })
      toast.success("Client suspendu")
      setReason("")
      onOpenChange(false)
    } catch (error) {
      toast.error(apiErrorMessage(error, "Suspension impossible"))
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Suspendre {clientName} ?</AlertDialogTitle>
          <AlertDialogDescription>
            Les utilisateurs du groupe ne pourront plus créer d'inventaires jusqu'à la réactivation. La raison est conservée dans
            le journal d'audit.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor="suspend-reason">Raison</Label>
          <Textarea id="suspend-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. : demande du client, litige de paiement…" />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          <Button variant="destructive" disabled={reason.trim().length < 3 || suspend.isPending} onClick={confirm}>
            {suspend.isPending ? "Suspension..." : "Suspendre"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function UsersCard({ client }: { client: SuperAdminClientDetail }) {
  const { data: currentUser } = useGetCurrentUser()
  const currentUserId = (currentUser as any)?.data?.id as number | undefined
  const [pendingUserId, setPendingUserId] = useState<number | null>(null)

  const impersonate = async (userId: number) => {
    setPendingUserId(userId)
    try {
      const result = await superAdminClientsService.impersonate(userId)
      startImpersonation(result.token, { ...result.user, expiresAt: result.expiresAt }, `/superadmin/clients/${client.id}`)
    } catch (error) {
      toast.error(apiErrorMessage(error, "Connexion impossible"))
      setPendingUserId(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Utilisateurs</CardTitle>
        <CardDescription>{client.counts.users} compte{client.counts.users > 1 ? "s" : ""} (hors comptes supprimés).</CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Profil</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Créé le</TableHead>
              <TableHead className="text-right">Support</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {client.users.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{[user.prenom, user.nom].filter(Boolean).join(" ") || "—"}</TableCell>
                <TableCell>{user.email ?? "—"}</TableCell>
                <TableCell>{user.profil ?? "—"}{user.is_superadmin ? " · superadmin" : ""}</TableCell>
                <TableCell>
                  <StatusBadge
                    tone={user.is_active === false ? "inactive" : "active"}
                    label={user.is_active === false ? "Inactif" : user.email_active ? "Actif" : "Email non vérifié"}
                  />
                </TableCell>
                <TableCell>{formatDate(user.created_at)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={user.is_superadmin || user.is_active === false || user.id === currentUserId || pendingUserId !== null}
                    title={
                      user.is_superadmin
                        ? "Impossible pour un autre superadmin"
                        : user.is_active === false
                          ? "Compte désactivé"
                          : "Voir l'application comme cet utilisateur (30 min)"
                    }
                    onClick={() => impersonate(user.id)}
                  >
                    <LogIn className="mr-1 h-4 w-4" />
                    {pendingUserId === user.id ? "Connexion..." : "Se connecter en tant que"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

function TraceEventsCard({ client }: { client: SuperAdminClientDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Parcours d'inscription et de paiement</CardTitle>
        <CardDescription>50 derniers événements (inscription, Stripe, emails, SMS).</CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {client.traceEvents.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun événement enregistré pour ce client.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Étape</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Erreur</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {client.traceEvents.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="whitespace-nowrap">{new Date(event.createdAt).toLocaleString("fr-FR")}</TableCell>
                  <TableCell>{event.stepKey}</TableCell>
                  <TableCell>{event.status}</TableCell>
                  <TableCell>{event.provider}</TableCell>
                  <TableCell>{event.email}</TableCell>
                  <TableCell className="max-w-xs truncate text-destructive" title={event.errorMessage ?? undefined}>
                    {event.errorMessage ?? ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
